import { useEffect, useState } from 'react';
import {
    Stack, Group, Title, Text, Button, Badge, Paper, SimpleGrid, Table,
    Loader, Center, Alert, ActionIcon, Divider, Modal,
} from '@mantine/core';
import {
    IconArrowLeft, IconAlertCircle, IconTool, IconCalendarPlus, IconAlertTriangle,
    IconClipboardCheck, IconBox,
} from '@tabler/icons-react';
import { authedFetch } from './session';

const STATUS_LABELS = {
    ordered: 'Besteld', in_build: 'In opbouw', delivered: 'Afgeleverd',
    in_service: 'In dienst', decommissioned: 'Uit dienst',
};
const SERVICE_TYPE = {
    maintenance: 'Onderhoud', repair: 'Reparatie', warranty: 'Garantie',
    recall: 'Terugroepactie', inspection: 'Inspectie',
};
const REPORT_STATUS = {
    draft: 'Concept', in_progress: 'In behandeling', customer_review: 'Klantcheck',
    accepted: 'Geaccepteerd', rejected: 'Afgewezen', archived: 'Gearchiveerd',
};
const DUE = {
    ok:      { color: 'green',  label: 'Op tijd' },
    soon:    { color: 'yellow', label: 'Binnenkort' },
    overdue: { color: 'red',    label: 'Te laat' },
};

function fmtDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('nl-NL');
}
function isPast(d) {
    if (!d) return false;
    const dt = new Date(d);
    return !Number.isNaN(dt.getTime()) && dt.getTime() < Date.now();
}

function InfoCard({ title, rows }) {
    return (
        <Paper withBorder p="md">
            <Text fw={600} size="sm" mb="xs">{title}</Text>
            <Stack gap={6}>
                {rows.map(([label, value, danger], i) => (
                    <Group key={i} justify="space-between" gap="xs" wrap="nowrap">
                        <Text size="sm" c="dimmed">{label}</Text>
                        <Text size="sm" fw={500} c={danger ? 'red' : undefined} ta="right">
                            {value ?? '—'}
                        </Text>
                    </Group>
                ))}
            </Stack>
        </Paper>
    );
}

export default function VehicleDetail({ ambulanceId, onBack, onSchedule }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await authedFetch(`/support/my/ambulances/${ambulanceId}`);
                setData(r);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [ambulanceId]);

    async function openReport(id) {
        setReportLoading(true);
        try {
            const r = await authedFetch(`/support/my/delivery-reports/${id}`);
            setReport(r);
        } catch (err) {
            setError(err.message);
        } finally {
            setReportLoading(false);
        }
    }

    if (loading) return <Center p="xl"><Loader /></Center>;
    if (error && !data) return <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert>;
    if (!data) return null;

    const a = data;
    const title = a.license_plate || a.known_as || `Voertuig #${a.id}`;
    const due = a.service_due;
    const records = a.service_records || [];
    const warranties = a.warranties || [];
    const buildSheet = a.build_sheet || [];
    const reports = a.delivery_reports || [];

    return (
        <Stack>
            <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                    <ActionIcon variant="subtle" onClick={onBack} title="Terug"><IconArrowLeft size={18} /></ActionIcon>
                    <div>
                        <Title order={4}>{title}</Title>
                        <Group gap="xs" mt={2}>
                            <Badge variant="light" color="gray">{STATUS_LABELS[a.status] || a.status}</Badge>
                            <Text size="sm" c="dimmed">
                                {`${a.base_vehicle_make || ''} ${a.base_vehicle_model || ''}`.trim()}
                            </Text>
                        </Group>
                    </div>
                </Group>
                <Button leftSection={<IconCalendarPlus size={16} />} onClick={() => onSchedule(a.id)}>
                    Service inplannen
                </Button>
            </Group>

            {Number(a.needs_attention) === 1 && (
                <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Aandacht vereist">
                    {a.attention_note || 'Dit voertuig vraagt aandacht.'}
                </Alert>
            )}

            {due && (
                <Alert
                    color={DUE[due.status]?.color || 'gray'}
                    icon={<IconTool size={16} />}
                    title={`Volgende onderhoudsbeurt — ${DUE[due.status]?.label || ''}`}
                >
                    <Group gap="xl">
                        <Text size="sm">Verwacht: <b>{fmtDate(due.next_due_date)}</b></Text>
                        {due.last_service_date && <Text size="sm">Laatste beurt: {fmtDate(due.last_service_date)}</Text>}
                        {due.interval_months ? <Text size="sm">Interval: {due.interval_months} mnd{due.interval_km ? ` / ${due.interval_km} km` : ''}</Text> : null}
                    </Group>
                </Alert>
            )}

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <InfoCard title="Identificatie" rows={[
                    ['Kenteken', a.license_plate],
                    ['Chassisnummer', a.chassis_number],
                    ['Projectnummer', a.project_number],
                    ['Roepnaam', a.known_as],
                    ['Homologatie', a.homologation_number],
                ]} />
                <InfoCard title="Voertuig" rows={[
                    ['Merk', a.base_vehicle_make],
                    ['Model', a.base_vehicle_model],
                    ['Bouwjaar', a.base_vehicle_year],
                    ['Status', STATUS_LABELS[a.status] || a.status],
                ]} />
                <InfoCard title="Certificering" rows={[
                    ['Norm', a.certification_standard],
                    ['Certificeringsdatum', fmtDate(a.certification_date)],
                    ['Verloopt', fmtDate(a.certification_expiry), isPast(a.certification_expiry)],
                ]} />
                <InfoCard title="Levering & garantie" rows={[
                    ['Afleverdatum', fmtDate(a.delivery_date)],
                    ['Garantie t/m', fmtDate(a.warranty_end_date), isPast(a.warranty_end_date)],
                ]} />
            </SimpleGrid>

            {/* Service history */}
            <Paper withBorder p="md">
                <Text fw={600} size="sm" mb="xs">Servicehistorie</Text>
                {records.length === 0 ? (
                    <Text size="sm" c="dimmed">Nog geen servicerapporten geregistreerd.</Text>
                ) : (
                    <Table.ScrollContainer minWidth={520}>
                        <Table verticalSpacing="xs" highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Datum</Table.Th><Table.Th>Type</Table.Th>
                                    <Table.Th>Omschrijving</Table.Th><Table.Th>Monteur</Table.Th><Table.Th>KM</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {records.map((r) => (
                                    <Table.Tr key={r.id}>
                                        <Table.Td>{fmtDate(r.service_date)}</Table.Td>
                                        <Table.Td><Badge size="sm" variant="light">{SERVICE_TYPE[r.service_type] || r.service_type}</Badge></Table.Td>
                                        <Table.Td><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{r.description || '—'}</Text></Table.Td>
                                        <Table.Td>{r.technician_name || '—'}</Table.Td>
                                        <Table.Td>{r.mileage_km ? `${r.mileage_km}` : '—'}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                )}
            </Paper>

            {/* Warranties */}
            {warranties.length > 0 && (
                <Paper withBorder p="md">
                    <Text fw={600} size="sm" mb="xs">Garanties</Text>
                    <Table verticalSpacing="xs">
                        <Table.Thead>
                            <Table.Tr><Table.Th>Type</Table.Th><Table.Th>Start</Table.Th><Table.Th>Einde</Table.Th></Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {warranties.map((w) => (
                                <Table.Tr key={w.id}>
                                    <Table.Td>{w.warranty_type || w.part_name || 'Garantie'}</Table.Td>
                                    <Table.Td>{fmtDate(w.warranty_start)}</Table.Td>
                                    <Table.Td><Text size="sm" c={isPast(w.warranty_end) ? 'red' : undefined}>{fmtDate(w.warranty_end)}</Text></Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>
            )}

            {/* Delivery reports */}
            <Paper withBorder p="md">
                <Text fw={600} size="sm" mb="xs">Opleverrapporten</Text>
                {reports.length === 0 ? (
                    <Text size="sm" c="dimmed">Nog geen opleverrapport beschikbaar.</Text>
                ) : (
                    <Stack gap="xs">
                        {reports.map((rep) => (
                            <Group key={rep.id} justify="space-between" wrap="nowrap">
                                <Group gap="xs">
                                    <IconClipboardCheck size={16} />
                                    <Text size="sm">Aflevering {fmtDate(rep.delivery_date)}</Text>
                                    <Badge size="sm" variant="light" color={rep.status === 'accepted' ? 'green' : 'gray'}>
                                        {REPORT_STATUS[rep.status] || rep.status}
                                    </Badge>
                                </Group>
                                <Button size="compact-xs" variant="light"
                                        loading={reportLoading} onClick={() => openReport(rep.id)}>
                                    Bekijk
                                </Button>
                            </Group>
                        ))}
                    </Stack>
                )}
            </Paper>

            {/* Build sheet — installed components */}
            <Paper withBorder p="md">
                <Group gap="xs" mb="xs">
                    <IconBox size={16} />
                    <Text fw={600} size="sm">Ingebouwde onderdelen</Text>
                </Group>
                {buildSheet.length === 0 ? (
                    <Text size="sm" c="dimmed">Geen onderdelen geregistreerd voor dit voertuig.</Text>
                ) : (
                    <Table.ScrollContainer minWidth={560}>
                        <Table verticalSpacing="xs" highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Onderdeel</Table.Th><Table.Th>Categorie</Table.Th>
                                    <Table.Th>Leverancier</Table.Th><Table.Th>Serienr.</Table.Th><Table.Th>Garantie t/m</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {buildSheet.map((b) => (
                                    <Table.Tr key={b.id}>
                                        <Table.Td>
                                            <Text size="sm" fw={500}>{b.label}</Text>
                                            {b.brand && <Text size="xs" c="dimmed">{b.brand}</Text>}
                                        </Table.Td>
                                        <Table.Td>{b.category || '—'}</Table.Td>
                                        <Table.Td>{b.supplier || '—'}</Table.Td>
                                        <Table.Td><Text size="xs" ff="monospace">{b.serial_number || '—'}</Text></Table.Td>
                                        <Table.Td>{b.warranty_end_date ? fmtDate(b.warranty_end_date) : (b.warranty_months ? `${b.warranty_months} mnd` : '—')}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                )}
            </Paper>

            {/* Delivery report detail modal */}
            <Modal opened={!!report} onClose={() => setReport(null)} size="lg"
                   title={report ? `Opleverrapport — aflevering ${fmtDate(report.delivery_date)}` : ''}>
                {report && <DeliveryReportView report={report} />}
            </Modal>
        </Stack>
    );
}

function DeliveryReportView({ report }) {
    const items = report.items || [];
    const extras = report.extras || [];
    const counts = items.reduce((acc, it) => {
        acc[it.status] = (acc[it.status] || 0) + 1;
        return acc;
    }, {});
    const openPoints = extras.filter((e) => e.extra_type === 'open_point');

    return (
        <Stack>
            <Group gap="xs">
                <Badge color="green" variant="light">{counts.ok || 0} OK</Badge>
                <Badge color="red" variant="light">{counts.not_ok || 0} Niet OK</Badge>
                <Badge color="gray" variant="light">{counts.na || 0} N.v.t.</Badge>
                <Badge color="orange" variant="light">{counts.open || 0} Open</Badge>
            </Group>

            {openPoints.length > 0 && (
                <>
                    <Divider label="Openstaande punten" labelPosition="left" />
                    <Stack gap={4}>
                        {openPoints.map((e) => (
                            <Group key={e.id} justify="space-between">
                                <Text size="sm">{e.label}{e.value ? ` — ${e.value}` : ''}</Text>
                                <Badge size="sm" variant="light" color={Number(e.done) === 1 ? 'green' : 'orange'}>
                                    {Number(e.done) === 1 ? 'Afgehandeld' : 'Open'}
                                </Badge>
                            </Group>
                        ))}
                    </Stack>
                </>
            )}

            {report.notes && (
                <>
                    <Divider label="Opmerkingen" labelPosition="left" />
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{report.notes}</Text>
                </>
            )}
        </Stack>
    );
}
